import * as React from 'react';

import { cn } from '@/core/lib/utils';

type InputFormat =
  | 'phone'
  | 'email'
  | 'alphanumeric'
  | 'numeric'
  | 'letters-only';

interface InputProps extends React.ComponentProps<'input'> {
  format?: InputFormat;
  minLength?: number;
  maxLength?: number;
}

// Format validation functions
const formatValidators: Record<InputFormat, (char: string) => boolean> = {
  phone: (char) => /[\d()+\-\s]/.test(char),
  email: (char) => /[\w@.-]/.test(char),
  alphanumeric: (char) => /[a-zA-Z0-9]/.test(char),
  numeric: (char) => /\d/.test(char),
  'letters-only': (char) => /[a-zA-Z]/.test(char),
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, onChange, format, minLength, maxLength, ...props },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.currentTarget.value;

      // Apply format validation if specified
      if (format && formatValidators[format]) {
        const validator = formatValidators[format];
        value = value
          .split('')
          .filter((char) => validator(char))
          .join('');
        e.currentTarget.value = value;
      }

      // Apply maxLength constraint
      if (typeof maxLength === 'number' && maxLength > 0) {
        if (value.length > maxLength) {
          e.currentTarget.value = value.slice(0, maxLength);
        }
      }

      // Validate minLength (doesn't prevent input, but marks validity)
      if (typeof minLength === 'number' && minLength > 0) {
        if (value.length < minLength) {
          e.currentTarget.setCustomValidity(
            `Minimum ${minLength} characters required`
          );
        } else {
          e.currentTarget.setCustomValidity('');
        }
      }

      onChange?.(e);
    };

    return (
      <input
        type={type}
        className={cn(
          // 'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          'w-full h-[40px] px-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default',
          className
        )}
        ref={ref}
        onChange={handleChange}
        data-pte-name='input-control'
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input };
